import React, { useState } from 'react';

import { EchoProbeResult, playbookLiveAudio } from '../../services/playbookLiveAudio';
import { Button } from '../ui/button';

interface EchoCheckCardProps {
  onReady: () => void;
  /** Rendered inside another panel, so it draws no card of its own. */
  embedded?: boolean;
}

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--hairline)',
  borderRadius: 12,
  padding: 20,
  maxWidth: 520,
};

/**
 * Pre-flight audio check.
 *
 * Unlike the turn-based training mode, the microphone here stays open while
 * the customer is talking. On laptop speakers that means the customer hears
 * itself, transcribes it as the salesperson, and the session degrades into the
 * model answering its own questions. This check catches that before a session
 * starts rather than after it is ruined.
 */
export const EchoCheckCard: React.FC<EchoCheckCardProps> = ({ onReady, embedded = false }) => {
  const [result, setResult] = useState<EchoProbeResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async (): Promise<void> => {
    setIsChecking(true);
    setError(null);
    try {
      setResult(await playbookLiveAudio.probeEcho());
    } catch {
      setError(
        'We could not access your microphone. Check your browser permissions and try again.'
      );
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div style={embedded ? undefined : CARD_STYLE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18, color: 'var(--ink)' }}>Before you start</h2>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ink-secondary)', lineHeight: 1.5 }}>
        This customer talks and listens at the same time, so your microphone stays on while
        they speak. <strong>Headphones are strongly recommended.</strong> On laptop speakers
        the customer can hear itself and start answering its own questions.
      </p>

      {error && (
        <div
          style={{
            background: 'var(--surface-subtle)',
            borderRadius: 8,
            padding: 12,
            fontSize: 13,
            color: 'var(--ink)',
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          style={{
            background: 'var(--surface-subtle)',
            borderRadius: 8,
            padding: 12,
            fontSize: 13,
            color: 'var(--ink)',
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {result.isEchoLikely ? (
            <>
              <strong>Your microphone is picking up your speakers.</strong> Plug in headphones
              before starting, or the customer will hear its own voice.
            </>
          ) : (
            <>
              <strong>Audio looks good.</strong> Your microphone is not picking up your speakers.
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="secondary" onClick={handleCheck} disabled={isChecking}>
          {isChecking ? 'Checking…' : 'Check my audio'}
        </Button>
        <Button onClick={onReady}>Start session</Button>
      </div>
    </div>
  );
};

export default EchoCheckCard;
